import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, getToken, setToken } from "./api";
import { analytics } from "./analytics";
import { applyTheme, getTheme } from "./theme";
import { isMobileDevice } from "./device";
import { forgetPushOnLogout } from "./push";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(!!getToken());
  const [mobileBlocked, setMobileBlocked] = useState(false);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setWorkspace(data.workspace);
      setMobileBlocked(data.user.mobile_allowed === false && isMobileDevice());
      analytics.setUser(data.user.id);
      analytics.setTracking(data.workspace.tracking);
      if (data.user.theme && data.user.theme !== getTheme()) applyTheme(data.user.theme);
    } catch (e) {
      if (e?.response?.data?.detail === "mobile_blocked") { setMobileBlocked(true); setLoading(false); return; }
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const onLogout = () => {
      setUser(null);
      setMobileBlocked(false);
      analytics.setUser(null);
    };
    const onBlocked = () => { setMobileBlocked(true); analytics.track("mobile_blocked_shown", { category: "auth" }); };
    window.addEventListener("tc:logout", onLogout);
    window.addEventListener("tc:mobile-blocked", onBlocked);
    return () => { window.removeEventListener("tc:logout", onLogout); window.removeEventListener("tc:mobile-blocked", onBlocked); };
  }, [refresh]);

  const login = useCallback(async (token, u) => {
    setToken(token);
    analytics.setUser(u.id);
    analytics.track("session_start", { category: "session" });
    setUser(u);
    await refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    analytics.track("logout", { category: "auth" });
    analytics.flush();
    await forgetPushOnLogout();
    setToken(null);
    setUser(null);
    setMobileBlocked(false);
    analytics.setUser(null);
  }, []);

  return <AuthContext.Provider value={{ user, setUser, workspace, loading, login, logout, refresh, mobileBlocked }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
